import React from "react";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import ReplayIcon from "@material-ui/icons/Replay";
import AllInclusiveIcon from "@material-ui/icons/AllInclusive";
import { LATAM_CURRENCIES, addDaysIso, flagEmoji, getNegocioFormat, money, moneyBoth, moneyFx, moneyPair, periodSlotCost, pinLengthOf, profitPercentOf, splitFromTyped, suggestedSalePrice, todayIso, withSuggestedSalePrice } from "./helpers";
import { HintTip, tNegocio } from "./shared";
import { COUNTRY_OPTIONS, fulfillmentOf, hasNoValidity, isMailbox, slotKindOf, usesPinSlots } from "./catalog";
import { useStyles } from "./styles";

export const DURATION_PRESETS = [14, 20, 30, 60, 90, 180, 365];

export const billingTypeOf = (account) => (account?.billingType === "autopay" ? "autopay" : "cost");

export const pinEnabledOf = (account) => {
  if (!account || account.saleType === "full") return false;
  if (!usesPinSlots(account)) return false;
  if (account.pinEnabled === false || account.pinEnabled === 0) return false;
  if (account.pinEnabled === true || account.pinEnabled === 1) return true;
  return (account.profiles || []).some((p) => String(p.pin || "").trim());
};

export const accountMoneyState = (account) => {
  const hasSale = account?.salePrice != null && account?.salePrice !== "";
  const hasCost = account?.cost != null && account?.cost !== "";
  const sale = hasSale ? moneyPair(account.salePrice, account.salePriceFx) : { local: "", fx: "" };
  const cost = hasCost ? moneyPair(account.cost, account.costFx) : { local: "", fx: "" };
  const side = (value) => (value === "fx" ? "fx" : "local");
  return {
    currency: side(account?.saleCurrency),
    saleCurrency: side(account?.saleCurrency),
    costCurrency: side(account?.costCurrency),
    salePrice: sale.local,
    salePriceFx: sale.fx,
    cost: cost.local,
    costFx: cost.fx,
  };
};

export const accountFormExtras = (account) => ({
  pinEnabled: pinEnabledOf(account),
  pinCustomEnabled: !!account?.pinCustom,
  pinCustom: account?.pinCustom || "",
  pinLength: pinLengthOf(account),
  billingType: billingTypeOf(account),
  originCountry: account?.originCountry || account?.product?.originCountry || "",
  deviceLimit: account?.deviceLimit != null ? account.deviceLimit : account?.product?.deviceLimit || "",
  lotSize: account?.lotSize || account?.product?.lotSize || 1,
  emailsBulk: "",
  slotKind: slotKindOf(account),
  fulfillmentType: account?.fulfillmentType || account?.product?.fulfillmentType || "credentials",
  loginPanel: !!(account?.loginPanel ?? account?.product?.loginPanel),
  botPublish: account?.botPublish !== false,
  ...accountMoneyState(account),
});

export const buildAccountPayload = (form) => {
  const autopay = form.billingType === "autopay";
  const pinEnabled = form.saleType === "profile" && usesPinSlots(form) && !!form.pinEnabled;
  return {
    productId: form.productId,
    status: form.status,
    saleType: slotKindOf(form) === "none" ? "full" : form.saleType,
    email: form.email,
    url: form.url || null,
    password: form.password || undefined,
    pinEnabled,
    pinLength: pinEnabled
      ? form.pinCustomEnabled
        ? String(form.pinCustom || "").length || 4
        : +form.pinLength || 4
      : 4,
    pinCustom: pinEnabled && form.pinCustomEnabled ? form.pinCustom : null,
    customProfileName: !!form.customProfileName,
    profileNamePrefix: form.customProfileName ? form.profileNamePrefix : null,
    billingType: autopay ? "autopay" : "cost",
    originCountry: form.originCountry || null,
    deviceLimit: form.deviceLimit !== "" && form.deviceLimit != null ? form.deviceLimit : null,
    lotSize: form.lotSize != null && form.lotSize !== "" ? Math.max(1, +form.lotSize) : 1,
    emailsBulk: form.emailsBulk || "",
    startDate: form.startDate || null,
    durationDays: hasNoValidity(form) ? 0 : form.durationDays,
    slotCount: form.slotCount != null && form.slotCount !== "" ? Math.max(0, +form.slotCount) : undefined,
    saleCurrency: form.saleCurrency || "local",
    salePrice: form.salePrice,
    salePriceFx: form.salePriceFx,
    costCurrency: form.costCurrency || "local",
    cost: form.cost,
    costFx: form.costFx,
    isRenewable: ![false, 0, "0", "false"].includes(form.isRenewable),
    notes: form.notes,
    supplierId: form.supplierId || null,
    botPublish: form.botPublish !== false,
  };
};

export const applyBillingType = (form, billingType) => {
  if (hasNoValidity(form)) return withSuggestedSalePrice(form, { billingType, durationDays: 0, endDate: "" });
  const startDate = form?.startDate || todayIso();
  const durationDays = form?.durationDays || (billingType === "autopay" ? 365 : 30);
  return withSuggestedSalePrice(form, {
    billingType,
    startDate,
    durationDays,
    endDate: billingType === "autopay" ? "" : addDaysIso(startDate, durationDays),
  });
};

export function BillingTypePick({ value, onChange }) {
  const classes = useStyles();
  const current = value === "autopay" ? "autopay" : "cost";
  return (
    <>
      <div className={classes.typeChoiceLabel}>{tNegocio("pages.accounts.billingType.title")}</div>
      <div className={classes.typePick}>
        <button
          type="button"
          className={`${classes.typeCard} ${current === "autopay" ? classes.typeCardOn : ""}`}
          onClick={() => onChange("autopay")}
        >
          <AllInclusiveIcon fontSize="small" />
          <strong style={{ display: "inline-flex", alignItems: "center" }}>
            {tNegocio("pages.accounts.billingType.autopay")}
            <HintTip text={tNegocio("pages.accounts.billingType.hintAutopay")} />
          </strong>
          <div className={classes.captionBlock}>{tNegocio("pages.accounts.billingType.autopaySub")}</div>
        </button>
        <button
          type="button"
          className={`${classes.typeCard} ${current === "cost" ? classes.typeCardOn : ""}`}
          onClick={() => onChange("cost")}
        >
          <ReplayIcon fontSize="small" />
          <strong style={{ display: "inline-flex", alignItems: "center" }}>
            {tNegocio("pages.accounts.billingType.cost")}
            <HintTip text={tNegocio("pages.accounts.billingType.hintCost")} />
          </strong>
          <div className={classes.captionBlock}>{tNegocio("pages.accounts.billingType.costSub")}</div>
        </button>
      </div>
    </>
  );
}

export function AccountPinFields({ form, onChange, showNames = true }) {
  const classes = useStyles();
  if (form?.saleType === "full" || slotKindOf(form) === "none") return null;
  if (!usesPinSlots(form)) {
    return showNames ? (
      <>
        <div className={classes.captionBlock}>{tNegocio(`pages.products.slotKinds.${slotKindOf(form)}Hint`)}</div>
        {showNames ? (
          <>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={!!form.customProfileName}
                  onChange={(e) => onChange({ customProfileName: e.target.checked })}
                />
              }
              label={tNegocio("fields.customProfileName")}
            />
            {form.customProfileName ? (
              <TextField
                fullWidth
                margin="dense"
                variant="outlined"
                label={tNegocio("fields.profileNamePrefix")}
                value={form.profileNamePrefix || ""}
                onChange={(e) => onChange({ profileNamePrefix: e.target.value })}
                helperText={tNegocio("pages.accounts.profileNameHint")}
              />
            ) : null}
          </>
        ) : null}
      </>
    ) : (
      <div className={classes.captionBlock}>{tNegocio(`pages.products.slotKinds.${slotKindOf(form)}Hint`)}</div>
    );
  }
  return (
    <>
      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={!!form.pinEnabled}
            onChange={(e) => onChange({ pinEnabled: e.target.checked, pinCustomEnabled: e.target.checked ? form.pinCustomEnabled : false })}
          />
        }
        label={tNegocio("fields.pinEnabled")}
      />
      {form.pinEnabled ? (
        <>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={!!form.pinCustomEnabled}
                onChange={(e) => onChange({ pinCustomEnabled: e.target.checked })}
              />
            }
            label={tNegocio("fields.pinCustom")}
          />
          {form.pinCustomEnabled ? (
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label={tNegocio("fields.pinCustom")}
              value={form.pinCustom || ""}
              onChange={(e) => onChange({ pinCustom: e.target.value.replace(/\D/g, "").slice(0, 20) })}
            />
          ) : (
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              type="number"
              label={tNegocio("fields.pinLength")}
              value={form.pinLength ?? 4}
              onChange={(e) => onChange({ pinLength: +e.target.value })}
            />
          )}
        </>
      ) : (
        <div className={classes.captionBlock}>{tNegocio("pages.accounts.pinDisabledHint")}</div>
      )}
      {showNames ? (
        <>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={!!form.customProfileName}
                onChange={(e) => onChange({ customProfileName: e.target.checked })}
              />
            }
            label={tNegocio("fields.customProfileName")}
          />
          {form.customProfileName ? (
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label={tNegocio("fields.profileNamePrefix")}
              value={form.profileNamePrefix || ""}
              onChange={(e) => onChange({ profileNamePrefix: e.target.value })}
              helperText={tNegocio("pages.accounts.profileNameHint")}
            />
          ) : null}
        </>
      ) : null}
    </>
  );
}

const parseMoneyDraft = (raw) => {
  const text = String(raw ?? "").trim().replace(/\s/g, "").replace(",", ".");
  if (text === "") return { empty: true, amount: null };
  if (!/^\d*\.?\d*$/.test(text) || text === ".") return { empty: false, amount: null };
  const amount = Number(text.endsWith(".") ? text.slice(0, -1) : text);
  return Number.isFinite(amount) ? { empty: false, amount } : { empty: false, amount: null };
};

const moneyInputText = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return String(Math.round(n * 100) / 100);
};

export function DualMoneyField({
  label,
  hint,
  helperText,
  localValue,
  fxValue,
  currency = "local",
  onChange,
  readOnly = false,
}) {
  const classes = useStyles();
  const fmt = getNegocioFormat();
  const editing = React.useRef(false);
  const [draft, setDraft] = React.useState(null);
  const cur = currency === "fx" ? "fx" : "local";
  const blank = localValue === "" || localValue == null;
  const pair = blank ? { local: "", fx: "" } : moneyPair(localValue, fxValue);
  const fromProps = blank ? "" : moneyInputText(cur === "fx" ? pair.fx : pair.local);
  const shown = draft != null ? draft : fromProps;
  React.useEffect(() => {
    if (!editing.current) setDraft(null);
  }, [fromProps, cur]);
  const commit = (raw) => {
    const parsed = parseMoneyDraft(raw);
    if (parsed.empty) {
      onChange({ local: "", fx: "", currency: cur, typed: "" });
      return;
    }
    if (parsed.amount == null) return;
    const next = splitFromTyped(parsed.amount, cur);
    onChange({ local: next.local, fx: next.fx, currency: cur, typed: String(raw) });
  };
  const other = blank ? "" : cur === "fx" ? money(pair.local) : moneyFx(pair.fx);
  const currencyTitle = (code) => {
    const c = (LATAM_CURRENCIES || []).find((row) => row.code === String(code || "").toUpperCase());
    if (!c) return String(code || "");
    return `${c.code} · ${c.name}`;
  };
  const localTitle = currencyTitle(fmt.currency || "COP");
  const fxTitle = currencyTitle(fmt.currencyFx || "USD");
  const otherName = cur === "fx" ? localTitle : fxTitle;
  const tip = hint || helperText;
  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: tip ? "150px 1fr 28px" : "150px 1fr",
          gap: 8,
          alignItems: "center",
        }}
      >
        <TextField
          select
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("pages.services.cart.currency")}
          value={cur}
          onChange={(e) => {
            const nextCur = e.target.value === "fx" ? "fx" : "local";
            if (blank) {
              onChange({ local: "", fx: "", currency: nextCur, typed: "" });
              return;
            }
            const hasPair =
              pair.local !== "" &&
              pair.fx !== "" &&
              Number(pair.local) > 0 &&
              Number(pair.fx) > 0;
            if (hasPair) {
              const view = nextCur === "fx" ? pair.fx : pair.local;
              onChange({
                local: pair.local,
                fx: pair.fx,
                currency: nextCur,
                typed: String(view),
              });
              return;
            }
            const amount = Number(shown);
            const next = splitFromTyped(Number.isFinite(amount) ? amount : 0, nextCur);
            onChange({ local: next.local, fx: next.fx, currency: nextCur, typed: String(shown) });
          }}
        >
          <MenuItem value="local">{localTitle}</MenuItem>
          <MenuItem value="fx">{fxTitle}</MenuItem>
        </TextField>
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          type="text"
          label={label}
          value={shown}
          onFocus={() => {
            editing.current = true;
            setDraft(fromProps);
          }}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw !== "" && !/^[\d.,\s]*$/.test(raw)) return;
            setDraft(raw);
            commit(raw);
          }}
          onBlur={() => {
            if (draft != null) commit(draft);
            editing.current = false;
            setDraft(null);
          }}
          InputProps={{ readOnly }}
          inputProps={{ inputMode: "decimal", min: 0 }}
        />
        {tip ? <HintTip text={tip} /> : null}
      </div>
      {blank ? null : (
        <div className={classes.captionBlock} style={{ margin: "-4px 0 8px" }}>
          {otherName}: {other}
        </div>
      )}
    </>
  );
}

export function AccountMoneyFields({ form, onChange, slotCount = 0 }) {
  const classes = useStyles();
  if (!form) return null;
  const slots = Math.max(1, Number(slotCount || 1));
  const unitCost = periodSlotCost({ ...form, cost: form.cost, slotCount: slots }, 30);
  const suggested = suggestedSalePrice({ ...form, cost: form.cost, slotCount: slots }, 30);
  const applyCost = ({ local, fx, currency }) =>
    onChange(
      withSuggestedSalePrice(form, {
        cost: local,
        costFx: fx,
        costCurrency: currency,
        slotCount: slots,
      })
    );
  return (
    <>
      <DualMoneyField
        label={tNegocio("pages.accounts.purchaseCost")}
        hint={tNegocio("pages.accounts.purchaseCostHint")}
        localValue={form.cost}
        fxValue={form.costFx}
        currency={form.costCurrency || "local"}
        onChange={applyCost}
      />
      {Number(form.cost || 0) > 0 && !hasNoValidity(form) ? (
        <div className={classes.captionBlock} style={{ margin: "-4px 0 8px", display: "inline-flex", alignItems: "center" }}>
          {tNegocio("pages.accounts.unitCost30")}: {moneyBoth(unitCost, null)}
          <HintTip text={tNegocio("pages.accounts.unitCost30Hint")} />
        </div>
      ) : null}
      {Number(unitCost || 0) > 0 ? (
        <div className={classes.captionBlock} style={{ margin: "-4px 0 8px" }}>
          {tNegocio("pages.accounts.suggestedSale")
            .replace("{price}", moneyBoth(suggested, null))
            .replace("{pct}", String(profitPercentOf()))}
        </div>
      ) : null}
      <DualMoneyField
        label={tNegocio("pages.accounts.salePrice")}
        hint={tNegocio("pages.accounts.salePriceHint")}
        localValue={form.salePrice}
        fxValue={form.salePriceFx}
        currency={form.saleCurrency || "local"}
        onChange={({ local, fx, currency }) =>
          onChange({ salePrice: local, salePriceFx: fx, saleCurrency: currency, currency })
        }
      />
      {isMailbox(form) ? (
        <>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            type="number"
            label={tNegocio("fields.lotSize")}
            value={form?.lotSize ?? 1}
            onChange={(e) => onChange({ lotSize: e.target.value })}
            helperText={tNegocio("pages.accounts.lotHint")}
          />
          {Number(form?.lotSize || 1) > 1 ? (
            <div className={classes.captionBlock} style={{ marginBottom: 8 }}>
              {tNegocio("pages.accounts.unitCost")}: {moneyBoth(Number(form.cost || 0) / Number(form.lotSize || 1), null)}
            </div>
          ) : null}
        </>
      ) : null}
    </>
  );
}

export function AccountDurationFields({ form, onChange }) {
  const classes = useStyles();
  if (hasNoValidity(form)) return null;
  const onDates = (startDate, durationDays) =>
    onChange(
      withSuggestedSalePrice(form, {
        startDate,
        durationDays,
        endDate: addDaysIso(startDate, durationDays),
      })
    );
  return (
    <>
      <FormControlLabel
        control={<Switch color="primary" checked={[false, 0, "0", "false"].includes(form?.isRenewable)} onChange={(event) => onChange({ isRenewable: !event.target.checked })} />}
        label="No renovable / un solo uso"
      />
      <TextField
        fullWidth
        margin="dense"
        variant="outlined"
        type="date"
        label={tNegocio("fields.startDate")}
        InputLabelProps={{ shrink: true }}
        value={form?.startDate || ""}
        onChange={(e) => onDates(e.target.value, form.durationDays)}
      />
      <div className={classes.filters} style={{ marginBottom: 8 }}>
        {DURATION_PRESETS.map((days) => (
          <Button
            key={days}
            size="small"
            color={Number(form?.durationDays) === days ? "primary" : "default"}
            variant={Number(form?.durationDays) === days ? "contained" : "outlined"}
            onClick={() => onDates(form.startDate, days)}
          >
            {tNegocio(`pages.accounts.durations.${days}`)}
          </Button>
        ))}
      </div>
      <TextField
        fullWidth
        margin="dense"
        variant="outlined"
        type="number"
        label={tNegocio("fields.days")}
        value={form?.durationDays ?? 30}
        onChange={(e) => onDates(form.startDate, +e.target.value)}
        helperText={form?.billingType === "autopay" ? tNegocio("pages.accounts.autopayDurationHint") : ""}
      />
      {form?.billingType === "autopay" ? null : (
      <TextField
        fullWidth
        margin="dense"
        variant="outlined"
        type="date"
        label={tNegocio("fields.endDate")}
        InputLabelProps={{ shrink: true }}
        value={form?.endDate || ""}
        InputProps={{ readOnly: true }}
      />
      )}
    </>
  );
}

export function AccountLicenseFields({ form, onChange }) {
  if (fulfillmentOf(form) !== "panel") return null;
  return (
    <TextField
      fullWidth
      margin="dense"
      variant="outlined"
      type="number"
      label={tNegocio("pages.accounts.licenseCount")}
      value={form?.slotCount ?? ""}
      onChange={(e) => onChange({ slotCount: e.target.value })}
      helperText={tNegocio("pages.accounts.licenseCountHint")}
    />
  );
}

export function AccountMailboxFields({ form, onChange }) {
  if (!isMailbox(form) || Number(form?.id) > 0 || form?.isNew === false) return null;
  return (
    <TextField
      fullWidth
      margin="dense"
      variant="outlined"
      multiline
      rows={5}
      label={tNegocio("fields.emailsBulk")}
      value={form?.emailsBulk || ""}
      onChange={(e) => onChange({ emailsBulk: e.target.value })}
      helperText={tNegocio("pages.accounts.emailsBulkHint")}
    />
  );
}

export function AccountOriginFields({ form, onChange }) {
  return (
    <>
      <TextField
        select
        fullWidth
        margin="dense"
        variant="outlined"
        label={tNegocio("fields.originCountry")}
        value={form?.originCountry || ""}
        onChange={(e) => onChange({ originCountry: e.target.value })}
      >
        <MenuItem value="">{tNegocio("fields.none")}</MenuItem>
        {COUNTRY_OPTIONS.map((row) => (
          <MenuItem key={row.code} value={row.code}>
            {flagEmoji(row.code)} {row.name}
          </MenuItem>
        ))}
      </TextField>
      {slotKindOf(form) === "device" ? (
        <TextField
          fullWidth
          margin="dense"
          variant="outlined"
          type="number"
          label={tNegocio("fields.deviceLimit")}
          value={form?.deviceLimit ?? ""}
          onChange={(e) => onChange({ deviceLimit: e.target.value })}
          helperText={tNegocio("pages.accounts.deviceLimitHint")}
        />
      ) : null}
    </>
  );
}
